import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  TwoWheeler as MotorcycleIcon,
  DirectionsCar as CarIcon,
  LocalShipping as TruckIcon,
  PedalBike as BikeIcon,
  VerifiedUser as VerifyIcon,
  Person as PersonIcon,
  Star as StarIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import DeliveryAgentVerification from '../../components/DeliveryAgentVerification/DeliveryAgentVerification';
import { useForm, Controller } from 'react-hook-form';
import { DeliveryPerson, DeliveryPersonStatus, VehicleType, DeliveryPersonFormData, UserRole } from '../../types/models';
import { deliveryPersonService, userService } from '../../services/firebase';

// Firebase data will be loaded dynamically

const DeliveryAgents: React.FC = () => {
  const [deliveryPersons, setDeliveryPersons] = useState<DeliveryPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<DeliveryPerson | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPerson, setSelectedPerson] = useState<DeliveryPerson | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<DeliveryPersonFormData>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      password: '',
      vehicleType: VehicleType.BICYCLE,
      vehicleNumber: '',
      licenseNumber: '',
      assignedAreas: []
    }
  });

  const loadDeliveryPersons = async (showLoading: boolean = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const deliveryPersonsData = await deliveryPersonService.getDeliveryPersons();
      
      if (Array.isArray(deliveryPersonsData)) {
        setDeliveryPersons(deliveryPersonsData);
      } else {
        setDeliveryPersons([]);
      }
    } catch (error) {
      setDeliveryPersons([]); // Reset to empty array on error
      if (showLoading) {
        setSnackbar({ open: true, message: 'Failed to load delivery agents', severity: 'error' });
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Auto-load delivery agents on component mount
  useEffect(() => {
    loadDeliveryPersons();
  }, []);

  const handleOpenDialog = (person?: DeliveryPerson) => {
    if (person) {
      setEditingPerson(person);
      reset({
        name: person.name,
        email: person.email,
        phone: person.phone,
        password: '', // Don't pre-fill password for editing
        vehicleType: person.vehicleType,
        vehicleNumber: person.vehicleNumber,
        licenseNumber: person.licenseNumber,
        assignedAreas: person.assignedAreas
      });
    } else {
      setEditingPerson(null);
      reset({
        name: '',
        email: '',
        phone: '',
        password: '',
        vehicleType: VehicleType.BICYCLE,
        vehicleNumber: '',
        licenseNumber: '',
        assignedAreas: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPerson(null);
    reset();
  };

  const onSubmit = async (data: DeliveryPersonFormData) => {
    try {
      if (editingPerson) {
        
        // Prepare update data for Firebase
        const updateData: Partial<DeliveryPerson> = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          vehicleType: data.vehicleType,
          vehicleNumber: data.vehicleNumber,
          licenseNumber: data.licenseNumber,
          assignedAreas: data.assignedAreas
        };
        
        await deliveryPersonService.updateDeliveryPerson(editingPerson.id, updateData);
        
        if (data.password && data.password.trim()) {
          try {
            const user = await userService.getUserByEmail(data.email);
            if (user) {
              setSnackbar({ 
                open: true, 
                message: 'Delivery agent updated successfully. Password update requires admin privileges.', 
                severity: 'warning' 
              });
            }
          } catch (error) {
          }
        } else {
          setSnackbar({ open: true, message: 'Delivery agent updated successfully', severity: 'success' });
        }
        
        await loadDeliveryPersons(false);
      } else {
        const userId = await userService.createUser({
          name: data.name,
          email: data.email,
          phone: data.phone,
          password: data.password,
          role: UserRole.DELIVERY,
          isActive: true
        });
        const newPersonData = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          vehicleType: data.vehicleType,
          vehicleNumber: data.vehicleNumber,
          licenseNumber: data.licenseNumber,
          assignedAreas: data.assignedAreas,
          status: DeliveryPersonStatus.AVAILABLE, // New agents start as available
          rating: 0,
          totalDeliveries: 0,
          totalEarnings: 0,
          joinDate: new Date(),
          isVerified: false,
          isActive: true
        };
        
        const deliveryPersonId = await deliveryPersonService.addDeliveryPerson(newPersonData);
        
        await userService.linkDeliveryPerson(userId, deliveryPersonId);
        
        await loadDeliveryPersons(false);
        setSnackbar({ 
          open: true, 
          message: 'Delivery agent created successfully with login credentials', 
          severity: 'success' 
        });
      }
      handleCloseDialog();
    } catch (error: any) {
      setSnackbar({ open: true, message: `Error saving delivery agent: ${error?.message || 'Unknown error'}`, severity: 'error' });
    }
  };

  const handleDelete = async (person: DeliveryPerson) => {
    if (window.confirm(`Are you sure you want to delete "${person.name}"?`)) {
      try {
        await deliveryPersonService.deleteDeliveryPerson(person.id);
        
        await loadDeliveryPersons(false);
        setSnackbar({ open: true, message: 'Delivery agent deleted successfully', severity: 'success' });
      } catch (error: any) {
        setSnackbar({ open: true, message: `Error deleting delivery agent: ${error?.message || 'Unknown error'}`, severity: 'error' });
      }
    }
    setAnchorEl(null);
  };

  const getVehicleIcon = (type: VehicleType) => {
    switch (type) {
      case VehicleType.BICYCLE: return <BikeIcon />;
      case VehicleType.MOTORCYCLE: return <MotorcycleIcon />;
      case VehicleType.CAR: return <CarIcon />;
      case VehicleType.VAN: return <TruckIcon />;
      default: return <BikeIcon />;
    }
  };

  const getStatusColor = (status: DeliveryPersonStatus) => {
    switch (status) {
      case DeliveryPersonStatus.AVAILABLE: return 'success';
      case DeliveryPersonStatus.BUSY: return 'warning';
      case DeliveryPersonStatus.OFFLINE: return 'default';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => `₹${amount.toLocaleString()}`;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Delivery Agents
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage delivery personnel and track their performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Add Delivery Agent
          </Button>
        </Box>
      </Box>

      {/* User Mapping Verification */}
      <Box sx={{ mb: 3 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VerifyIcon color="primary" />
              <Typography variant="h6">User Account Verification</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <DeliveryAgentVerification />
          </AccordionDetails>
        </Accordion>
      </Box>

      {loading && (
        <Box sx={{ mb: 2, p: 1, bgcolor: 'info.light', borderRadius: 1, fontSize: '0.8rem' }}>
          <Typography variant="caption">
            Debug: {deliveryPersons.length} agents | Loading: {loading.toString()} | Dialog: {dialogOpen.toString()}
          </Typography>
        </Box>
      )}

      {/* Delivery Agents Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Performance</TableCell>
                <TableCell>Earnings</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveryPersons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {loading ? 'Loading delivery agents...' : 'No delivery agents found. Click "Load Agents" to refresh or "Add Delivery Agent" to create one.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                deliveryPersons
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((person) => (
                  <TableRow key={person.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ width: 48, height: 48, mr: 2, bgcolor: 'primary.main' }}>
                          {person.profileImage ? (
                            <img src={person.profileImage} alt={person.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <PersonIcon />
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {person.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Joined {person.joinDate.toLocaleDateString()}
                          </Typography>
                          {person.isVerified && (
                            <Chip label="Verified" size="small" color="success" sx={{ ml: 1, height: 20 }} />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{person.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{person.phone}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getVehicleIcon(person.vehicleType)}
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {person.vehicleType}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {person.vehicleNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={person.status.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        size="small"
                        color={getStatusColor(person.status) as any}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <StarIcon sx={{ fontSize: 16, color: 'warning.main', mr: 0.5 }} />
                        <Typography variant="body2">{person.rating.toFixed(1)}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {person.totalDeliveries} deliveries
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(person.totalEarnings)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setSelectedPerson(person);
                        }}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={deliveryPersons.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          handleOpenDialog(selectedPerson!);
          setAnchorEl(null);
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedPerson!)} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingPerson ? 'Edit Delivery Agent' : 'Add New Delivery Agent'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: 'Name is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Full Name"
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{ required: 'Email is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email"
                        type="email"
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{ required: 'Phone is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone Number"
                        fullWidth
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ 
                      required: editingPerson ? false : 'Password is required for new agents',
                      minLength: editingPerson ? 0 : { value: 6, message: 'Password must be at least 6 characters' }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={editingPerson ? "New Password (optional)" : "Password"}
                        type="password"
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message || (editingPerson ? "Leave blank to keep current password" : "")}
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Controller
                    name="vehicleType"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Vehicle Type</InputLabel>
                        <Select {...field} label="Vehicle Type">
                          {Object.values(VehicleType).map((type) => (
                            <MenuItem key={type} value={type}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Controller
                    name="vehicleNumber"
                    control={control}
                    rules={{ required: 'Vehicle number is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Vehicle Number"
                        fullWidth
                        error={!!errors.vehicleNumber}
                        helperText={errors.vehicleNumber?.message}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <Controller
                    name="licenseNumber"
                    control={control}
                    rules={{ required: 'License number is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="License Number"
                        fullWidth
                        error={!!errors.licenseNumber}
                        helperText={errors.licenseNumber?.message}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {editingPerson ? 'Update' : 'Create'} Agent
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeliveryAgents;
